import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { App } from 'antd';
import { auth, type User } from '../services/auth';
import {
  History,
  LogOut,
  MessageCircle,
  Settings,
  Trophy,
  FileText,
  Wand2,
  Edit3,
  Sparkles,
} from 'lucide-react';
import AuthModal from './AuthModal';
import { logo } from '../assets/images';

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  const { message: antdMessage } = App.useApp();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>(
    'login',
  );
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);

  // 初始化认证状态
  // 同步更新 locationRef
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      // 如果auth中已有用户信息，直接使用，不再重新获取
      const currentUser = auth.getUser();
      if (currentUser) {
        if (mounted) {
          setUser(currentUser);
          setIsLoading(false);
        }
        return;
      }

      // 只有在没有用户信息时才调用init
      const isAuthenticated = await auth.init();
      if (mounted) {
        if (isAuthenticated) {
          setUser(auth.getUser());
        } else {
          // 认证失败，如果在需要登录的页面，重定向到首页
          setUser(null);
          const publicPaths = [
            '/',
            '/login',
            '/register',
            '/resume-generate',
            '/resume-edit',
            '/resume-optimize',
          ];
          if (!publicPaths.includes(locationRef.current.pathname)) {
            navigate('/', { replace: true });
          }
        }
        setIsLoading(false);
      }
    };

    initAuth();

    // 订阅认证状态变化
    const unsubscribe = auth.subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        setUser(auth.getUser());
      } else {
        setUser(null);
        // 如果在需要登录的页面（非首页），重定向到首页
        const publicPaths = [
          '/',
          '/login',
          '/register',
          '/resume-generate',
          '/resume-edit',
          '/resume-optimize',
        ];
        if (!publicPaths.includes(locationRef.current.pathname)) {
          navigate('/', { replace: true });
        }
      }
    });

    // 监听滚动事件，添加背景效果
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      mounted = false;
      window.removeEventListener('scroll', handleScroll);
      unsubscribe();
    };
  }, [navigate]);

  const handleLogin = () => {
    setAuthModalMode('login');
    setIsAuthModalOpen(true);
  };

  const handleRegister = () => {
    setAuthModalMode('register');
    setIsAuthModalOpen(true);
  };

  const handleLogout = async () => {
    // 立即关闭确认弹窗，提升用户体验
    setIsLogoutConfirmOpen(false);
    await auth.logout();
    setUser(null);
    setIsUserMenuOpen(false);
    antdMessage.success('退出登录成功');
  };

  const handleLogoutConfirm = () => {
    setIsUserMenuOpen(false);
    setIsLogoutConfirmOpen(true);
  };

  const handleLogoutCancel = () => {
    setIsLogoutConfirmOpen(false);
  };

  const handleMenuAction = (action: string) => {
    setIsUserMenuOpen(false);
    switch (action) {
      case 'settings':
        navigate('/settings');
        break;
      case 'history':
        navigate('/history');
        break;
      case 'logout':
        handleLogoutConfirm();
        break;
    }
  };

  const handleAuthModalClose = () => {
    setIsAuthModalOpen(false);
    const currentUser = auth.getUser();
    if (currentUser) {
      setUser(currentUser);
    }
  };

  return (
    <>
      <nav
        className={`
          fixed top-0 left-0 right-0 z-50
          transition-all duration-500 ease-out
          ${
            isScrolled
              ? 'bg-[#0A0E1A]/80 backdrop-blur-xl shadow-lg shadow-black/20'
              : 'bg-transparent'
          }
        `}
      >
        {/* 底部边框渐变 */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />

        {/* 微妙的发光效果 */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-32 bg-amber-500/5 blur-[80px] rounded-full" />
          <div className="absolute top-0 right-1/4 w-64 h-24 bg-emerald-500/5 blur-[60px] rounded-full" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo 区域 */}
            <button
              onClick={() => navigate('/')}
              className="group flex items-center gap-2 cursor-pointer"
            >
              {/* Logo 图标容器 */}
              <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0">
                <img
                  src={logo}
                  alt="面面帮"
                  className="w-full h-full object-contain"
                />
              </div>

              {/* 品牌文字 */}
              <div className="flex flex-col -ml-1">
                <span className="text-white font-bold text-lg tracking-tight">
                  面面帮
                </span>
              </div>
            </button>

            {/* 中间导航区域 - 简历功能 */}
            <div className="hidden md:flex items-center gap-10">
              {/* 简历生成 */}
              <button
                onClick={() => navigate('/resume-generate')}
                className={`text-sm font-medium transition-all duration-200 cursor-pointer ${
                  location.pathname === '/resume-generate'
                    ? 'text-white border-b-2 border-amber-500 pb-1'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                简历生成
              </button>

              {/* 简历编辑 */}
              <button
                onClick={() => navigate('/resume-edit')}
                className={`text-sm font-medium transition-all duration-200 cursor-pointer ${
                  location.pathname === '/resume-edit'
                    ? 'text-white border-b-2 border-amber-500 pb-1'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                简历编辑
              </button>

              {/* 简历评估 */}
              <button
                onClick={() => navigate('/resume-optimize')}
                className={`text-sm font-medium transition-all duration-200 cursor-pointer ${
                  location.pathname === '/resume-optimize'
                    ? 'text-white border-b-2 border-amber-500 pb-1'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                简历评估
              </button>
            </div>

            {/* 右侧操作区域 */}
            <div className="flex items-center gap-2 md:gap-4">
              {isLoading ? (
                // 加载状态 - 与头像框大小保持一致
                <div className="relative p-1">
                  <div className="w-9 h-9 md:w-10 md:h-10 rounded-full border-2 border-slate-600 border-t-amber-500 animate-spin" />
                </div>
              ) : user ? (
                // 已登录状态
                <div className="flex items-center gap-2 md:gap-3">
                  {/* 排行榜入口 */}
                  <button
                    onClick={() => navigate('/leaderboard')}
                    className="group relative p-2 rounded-xl
                      text-amber-400
                      hover:bg-amber-500/10
                      transition-all duration-300 ease-out
                      cursor-pointer"
                  >
                    <Trophy className="w-5 h-5" />
                    {/* 悬停提示 */}
                    <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-slate-800 text-slate-300 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                      排行榜
                    </span>
                  </button>

                  {/* 聊一聊入口 */}
                  <button
                    onClick={() => navigate('/chat')}
                    className="group relative p-2 rounded-xl
                      text-emerald-400
                      hover:bg-emerald-500/10
                      transition-all duration-300 ease-out
                      cursor-pointer"
                  >
                    <MessageCircle className="w-5 h-5" />
                    {/* 悬停提示 */}
                    <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-slate-800 text-slate-300 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                      聊一聊
                    </span>
                  </button>

                  {/* 用户头像和菜单 - hover触发 */}
                  <div
                    className="relative group"
                    onMouseEnter={() => setIsUserMenuOpen(true)}
                    onMouseLeave={() => setIsUserMenuOpen(false)}
                  >
                    {/* 头像区域 */}
                    <div className="relative p-1">
                      <img
                        src={user?.avatar}
                        alt="avatar"
                        className={`
                          w-9 h-9 md:w-10 md:h-10 rounded-full border-2 transition-all duration-300
                          ${
                            isUserMenuOpen
                              ? 'border-amber-500'
                              : 'border-amber-500/30 hover:border-amber-500/60'
                          }
                        `}
                      />
                    </div>

                    {/* 用户菜单下拉 - 美化设计 */}
                    <div
                      className={`
                        absolute right-0 top-full pt-3 w-48 z-50
                        transition-all duration-300 ease-out
                        ${
                          isUserMenuOpen
                            ? 'opacity-100 visible translate-y-0'
                            : 'opacity-0 invisible -translate-y-2 pointer-events-none'
                        }
                      `}
                    >
                      <div className="relative">
                        {/* 菜单面板 */}
                        <div
                          className="
                          relative overflow-hidden rounded-xl
                          border border-white/10 
                          bg-[#1E293B]/90 backdrop-blur-xl
                          shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.05)]
                        "
                        >
                          {/* 顶部渐变装饰 */}
                          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-500 via-emerald-500 to-amber-500 opacity-60" />

                          {/* 菜单列表 */}
                          <ul className="py-2">
                            {/* 面试历史 */}
                            <li>
                              <button
                                onClick={() => handleMenuAction('history')}
                                className="
                                  group/item w-full flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg
                                  text-sm text-slate-300 font-medium cursor-pointer
                                  transition-all duration-200
                                  hover:bg-amber-500/10 hover:text-amber-400
                                "
                              >
                                <History className="w-4 h-4 text-amber-400/70 group-hover/item:text-amber-400 transition-colors" />
                                <span>面试历史</span>
                              </button>
                            </li>

                            {/* 个人设置 */}
                            <li>
                              <button
                                onClick={() => handleMenuAction('settings')}
                                className="
                                  group/item w-full flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg
                                  text-sm text-slate-300 font-medium cursor-pointer
                                  transition-all duration-200
                                  hover:bg-emerald-500/10 hover:text-emerald-400
                                "
                              >
                                <Settings className="w-4 h-4 text-emerald-400/70 group-hover/item:text-emerald-400 transition-colors" />
                                <span>个人设置</span>
                              </button>
                            </li>

                            {/* 分隔线 */}
                            <li className="my-2 mx-4 border-t border-white/5" />

                            {/* 退出登录 */}
                            <li>
                              <button
                                onClick={() => handleMenuAction('logout')}
                                className="
                                  group/item w-full flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg
                                  text-sm text-slate-300 font-medium cursor-pointer
                                  transition-all duration-200
                                  hover:bg-red-500/10 hover:text-red-400
                                "
                              >
                                <LogOut className="w-4 h-4 text-red-400/70 group-hover/item:text-red-400 transition-colors" />
                                <span>退出登录</span>
                              </button>
                            </li>
                          </ul>
                        </div>

                        {/* 小三角箭头 */}
                        <div
                          className="
                          absolute -top-1.5 right-4 w-3 h-3 
                          bg-[#1E293B]/90 border-l border-t border-white/10 
                          rotate-45 backdrop-blur-xl
                        "
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // 未登录状态
                <div className="flex items-center gap-2 md:gap-3">
                  {/* 登录按钮 - 幽灵按钮风格 */}
                  <button
                    onClick={handleLogin}
                    className="group relative px-4 md:px-6 py-2 md:py-2.5 rounded-xl
                      text-slate-300 hover:text-white text-sm font-medium
                      border border-amber-500/30 hover:border-amber-400/60
                      bg-transparent hover:bg-amber-500/10
                      transition-all duration-300 ease-out
                      overflow-hidden
                      active:scale-95 cursor-pointer"
                  >
                    {/* 背景发光效果 */}
                    <div
                      className="absolute inset-0 bg-gradient-to-r from-amber-500/0 via-amber-500/10 to-amber-500/0 
                      translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"
                    />
                    <span className="relative z-10">登录</span>
                  </button>

                  {/* 注册按钮 - 渐变背景 */}
                  <button
                    onClick={handleRegister}
                    className="group relative px-4 md:px-6 py-2 md:py-2.5 rounded-xl
                      text-white text-sm font-semibold
                      bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500
                      bg-[length:200%_100%]
                      transition-all duration-300 ease-out
                      hover:bg-[position:100%_0]
                      active:scale-95
                      overflow-hidden cursor-pointer"
                  >
                    {/* 光泽扫过效果 */}
                    <div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent 
                      -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out"
                    />
                    <span className="relative z-10 flex items-center gap-1.5">
                      注册
                      <svg
                        className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* 认证弹窗 */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={handleAuthModalClose}
        defaultMode={authModalMode}
        onSuccess={async () => {
          // 登录/注册成功后刷新用户信息
          const user = await auth.fetchUser();
          if (user) {
            setUser(user);
          }
        }}
      />

      {/* 退出登录确认弹窗 */}
      {isLogoutConfirmOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-fade-in">
          {/* 背景遮罩 */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

          {/* 确认弹窗 */}
          <div className="relative w-full max-w-sm bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 shadow-2xl overflow-hidden animate-scale-in">
            {/* 顶部装饰 */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 via-orange-500 to-red-500" />

            <div className="p-6">
              {/* 图标和标题 */}
              <div className="text-center mb-6">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                  <LogOut className="w-7 h-7 text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  确认退出登录？
                </h3>
              </div>

              {/* 按钮组 */}
              <div className="flex gap-3">
                <button
                  onClick={handleLogoutCancel}
                  className="flex-1 py-2.5 px-4 rounded-xl
                    text-gray-300 font-medium
                    border border-gray-600 hover:border-gray-500
                    bg-gray-700/50 hover:bg-gray-700
                    transition-all duration-200
                    cursor-pointer"
                >
                  取消
                </button>
                <button
                  onClick={handleLogout}
                  className="flex-1 py-2.5 px-4 rounded-xl
                    text-white font-medium
                    bg-gradient-to-r from-red-600 to-red-500
                    hover:from-red-500 hover:to-red-400
                    transition-all duration-200
                    cursor-pointer"
                >
                  确认
                </button>
              </div>
            </div>
          </div>

          {/* 动画样式 */}
          <style>{`
            @keyframes fade-in {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes scale-in {
              from {
                opacity: 0;
                transform: scale(0.95) translateY(10px);
              }
              to {
                opacity: 1;
                transform: scale(1) translateY(0);
              }
            }
            .animate-fade-in {
              animation: fade-in 0.2s ease-out forwards;
            }
            .animate-scale-in {
              animation: scale-in 0.3s ease-out forwards;
            }
          `}</style>
        </div>
      )}
    </>
  );
}
